import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../api/client';
import { AnalyticsData } from '../../types';
import { MetricCard } from '../../components/common/MetricCard';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import { TrendingUp, DollarSign, Users, AlertTriangle, Camera, RefreshCw } from 'lucide-react';

const PIE_COLORS = ['#38bdf8', '#818cf8', '#34d399', '#fbbf24', '#f472b6', '#a78bfa'];

const money = (n: number, ccy = 'USD') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: ccy || 'USD' }).format(n || 0);

export const AnalyticsPage: React.FC = () => {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.getAnalytics());
    } catch (err) {
      console.error('Failed to load analytics', err);
      setError('Could not load analytics. Check that you are signed in as an admin.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const ccy = data?.currency || 'USD';
  const pie = (data?.planBreakdown ?? []).filter((p) => p.mrr > 0).map((p, i) => ({
    name: p.plan,
    value: Number(p.mrr.toFixed(2)),
    color: PIE_COLORS[i % PIE_COLORS.length],
  }));

  return (
    <div>
      <div
        style={{
          background: 'var(--bg-surface-card)', border: '1px solid var(--border-dim)',
          borderRadius: 'var(--radius-lg)', padding: '16px 20px', marginBottom: '20px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
            <TrendingUp size={18} color="var(--primary-light)" />
            <h2 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-main)' }}>
              Platform Revenue &amp; Fleet Analytics
            </h2>
          </div>
          <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
            Every figure is computed live from the payment &amp; subscription ledger and the device fleet.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', padding: '6px 12px', borderRadius: '6px', cursor: loading ? 'default' : 'pointer', border: '1px solid var(--border-dim)', background: 'transparent', color: 'var(--text-muted)' }}
        >
          <RefreshCw size={13} className={loading ? 'spin' : ''} /> Refresh
        </button>
      </div>

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.35)', color: 'var(--danger-text)', borderRadius: 'var(--radius-lg)', padding: '10px 16px', marginBottom: '16px', fontSize: '13px' }}>{error}</div>
      )}

      {loading && !data ? (
        <div style={{ padding: '24px', color: 'var(--text-dim)', fontSize: '14px' }}>Loading analytics…</div>
      ) : data ? (
        <>
          <div className="kpi-grid">
            <MetricCard
              label="Monthly Recurring Revenue (MRR)"
              value={money(data.mrr, ccy)}
              source="Active subscriptions × plan price (yearly ÷ 12)"
              icon={<DollarSign size={18} />}
              subtext={`${data.activeSubscriptions} active subscriptions`}
            />
            <MetricCard
              label="Average Revenue Per User (ARPU)"
              value={money(data.arpu, ccy)}
              source="MRR ÷ paying accounts"
              icon={<Users size={18} />}
              subtext={`${data.payingAccounts} paying · ${data.customers} accounts total`}
            />
            <MetricCard
              label="Payment Failure Rate (30d)"
              value={`${data.paymentFailureRate.toFixed(1)}%`}
              source="Failed vs succeeded, last 30 days"
              icon={<AlertTriangle size={18} />}
              subtext={`${data.failed30d} failed · ${data.succeeded30d} succeeded`}
            />
            <MetricCard
              label="Cameras Online"
              value={`${data.fleet.online} / ${data.fleet.total}`}
              source="Heartbeat within 3 minutes"
              icon={<Camera size={18} />}
              subtext={`${data.fleet.offline} offline · ${data.canceled90d} cancellations (90d)`}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(480px, 1fr))', gap: '20px', marginBottom: '20px' }}>
            <div className="card">
              <h3 className="card-title" style={{ marginBottom: '4px' }}>Collected revenue</h3>
              <div className="card-subtitle" style={{ marginBottom: '16px' }}>
                Subscriptions + hardware sales per month, last 6 months
              </div>
              <div style={{ height: '280px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.revenueTrend}>
                    <defs>
                      <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#38bdf8" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#38bdf8" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} />
                    <Tooltip
                      formatter={(v: number) => money(v, ccy)}
                      contentStyle={{ backgroundColor: '#0f172a', borderColor: '#334155', borderRadius: '8px', color: '#fff' }}
                    />
                    <Area type="monotone" dataKey="revenue" stroke="#38bdf8" strokeWidth={2} fillOpacity={1} fill="url(#colorRev)" name="Revenue" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="card">
              <h3 className="card-title" style={{ marginBottom: '4px' }}>MRR by plan</h3>
              <div className="card-subtitle" style={{ marginBottom: '16px' }}>
                Monthly recurring revenue share per plan tier
              </div>
              <div style={{ height: '280px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {pie.length === 0 ? (
                  <span style={{ color: 'var(--text-dim)', fontSize: '13px' }}>No paid subscriptions yet.</span>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pie} cx="50%" cy="50%" innerRadius={60} outerRadius={95} paddingAngle={5}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
                      >
                        {pie.map((entry, i) => (<Cell key={i} fill={entry.color} />))}
                      </Pie>
                      <Tooltip
                        formatter={(v: number) => money(v, ccy)}
                        contentStyle={{ backgroundColor: '#0f172a', borderColor: '#334155', borderRadius: '8px', color: '#fff' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>

          {/* Plan table + outstanding */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '20px' }}>
            <div className="card">
              <h3 className="card-title" style={{ marginBottom: '16px' }}>Subscriptions by plan</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {data.planBreakdown.length === 0 ? (
                  <span style={{ color: 'var(--text-dim)', fontSize: '13px' }}>No active subscriptions.</span>
                ) : data.planBreakdown.map((p) => (
                  <div key={p.slug || p.plan} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'rgba(15,23,42,0.6)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
                    <span style={{ fontSize: '13px', color: 'var(--text-main)' }}>{p.plan}</span>
                    <span style={{ fontSize: '12.5px', color: 'var(--text-muted)' }}>
                      {p.subscribers} sub{p.subscribers === 1 ? '' : 's'} · {money(p.mrr, ccy)}/mo
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="card">
              <h3 className="card-title" style={{ marginBottom: '16px' }}>Cash position</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Subscriptions collected</span>
                  <strong style={{ fontSize: '13px', color: 'var(--text-main)' }}>{money(data.paidToDate, ccy)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                    Hardware sales collected{data.hardwareOrders ? ` (${data.hardwareOrders})` : ''}
                  </span>
                  <strong style={{ fontSize: '13px', color: 'var(--text-main)' }}>{money(data.hardwareCollected, ccy)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--border-subtle)', paddingTop: '10px' }}>
                  <span style={{ fontSize: '13px', color: 'var(--text-main)', fontWeight: 600 }}>Total collected</span>
                  <strong style={{ fontSize: '14px', color: 'var(--success-text)' }}>{money(data.totalCollected, ccy)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                  <span style={{ fontSize: '12.5px', color: 'var(--text-dim)' }}>In unfinished checkouts (sub + hardware)</span>
                  <span style={{ fontSize: '12.5px', color: 'var(--text-dim)' }}>{money(data.pendingAmount + data.hardwarePending, ccy)}</span>
                </div>
                <div style={{ fontSize: '11.5px', color: 'var(--text-dim)', borderTop: '1px solid var(--border-subtle)', paddingTop: '10px' }}>
                  MRR above is recurring only. Hardware is one-time; camera-as-a-service, when launched, is recurring and will show in MRR automatically.
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};
